/**
 * @author  Agence Dn'D <contact@dnd.fr>
 * @license https://opensource.org/licenses/osl-3.0.php Open Software License (OSL 3.0)
 * @link    https://www.dnd.fr/
 */

/**
 * Handles the display and update of product quantity batch information based on the selected variant.
 * Listens for variant changes and updates the batch quantity display accordingly.
 *
 * Usage: Instantiated automatically on script load.
 */
class SesasopQuantityBatch {
  /**
   * Initializes the SesasopQuantityBatch instance and sets up event listeners.
   */
  constructor() {
    this.quantityBatchDisplay = null;
    this.quantityBatchValue = null;
    this.variantData = null;
    this.currentVariantId = null;

    this.init();
  }

  /**
   * Initializes the setup process, waiting for DOMContentLoaded if necessary.
   */
  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Sets up DOM references and event listeners. Loads variant data from the global window object.
   */
  setup() {
    this.productForm = document.querySelector('product-form');

    if (!this.productForm || !this.productForm.dataset.variantQuantities) return;

    this.quantityBatchDisplay = document.getElementById('quantity-batch-display');
    this.quantityBatchValue = document.getElementById('quantity-batch-value');

    if (!this.quantityBatchDisplay || !this.quantityBatchValue) return;

    try {
      this.variantData = JSON.parse(this.productForm.dataset.variantQuantities);
    } catch (e) {
      console.error('Error parsing variant quantities data:', e);
      return;
    }

    const currentVariantInput = this.productForm.querySelector('[name="id"]');
    this.currentVariantId = currentVariantInput ? currentVariantInput.value : null;

    if (this.currentVariantId) {
      this.updateQuantityBatch(this.currentVariantId.toString());
    }

    this.attachEventListeners();
  }

  /**
   * Updates the displayed batch quantity for the given variant ID.
   * @param {string} variantId - The ID of the variant to update the batch quantity for.
   */
  updateQuantityBatch(variantId) {
    const batchQuantity = this.variantData[variantId];

    if (batchQuantity) {
      this.quantityBatchValue.textContent = batchQuantity;
      this.quantityBatchDisplay.style.display = 'block';
    } else {
      this.quantityBatchDisplay.style.display = 'none';
    }
  }

  /**
   * Attaches event listeners for variant changes and input changes.
   */
  attachEventListeners() {
    document.addEventListener('change', (event) => {
      if (event.target.matches('input[name="id"], select[name="id"]')) {
        this.updateQuantityBatch(event.target.value);
      }
    });

    document.addEventListener('variant:change', (event) => {
      if (event.detail && event.detail.variant && event.detail.variant.id) {
        this.updateQuantityBatch(event.detail.variant.id.toString());
      }
    });
  }

  /**
   * Forces an update of the batch quantity display for a specific variant ID.
   * @param {string|number} variantId - The ID of the variant to force update.
   */
  forceUpdate(variantId) {
    if (this.variantData && variantId) {
      this.updateQuantityBatch(variantId.toString());
    }
  }

  /**
   * Destroys the instance by clearing all references.
   */
  destroy() {
    this.quantityBatchDisplay = null;
    this.quantityBatchValue = null;
    this.variantData = null;
    this.currentVariantId = null;
  }
}

// Instantiates the quantity batch handler on script load.
new SesasopQuantityBatch();
